export const PAUSE_REASONS = Object.freeze([
  "explicit",
  "visibility-hidden",
  "blur",
  "suspend",
  "reload",
] as const);

export type PauseReason = (typeof PAUSE_REASONS)[number];

export type NormalizedLifecycleEvent =
  | Readonly<{ type: "pause"; reason: PauseReason }>
  | Readonly<{ type: "resume"; monotonicOriginMs: number }>;

export type HeldAxis = -1 | 0 | 1;

export interface NormalizedHeldInput {
  readonly type: "normalized-held-input";
  readonly movementX: HeldAxis;
  readonly movementZ: HeldAxis;
  readonly padCharge: boolean;
}

export const EMPTY_HELD_INPUT: Readonly<NormalizedHeldInput> = Object.freeze({
  type: "normalized-held-input",
  movementX: 0,
  movementZ: 0,
  padCharge: false,
});

function isExactRecord(value: unknown, expectedKeys: readonly string[]): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value) as object | null;
  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }
  const ownKeys = Reflect.ownKeys(value);
  if (ownKeys.some((key) => typeof key !== "string")) {
    return false;
  }
  const keys = (ownKeys as string[]).sort();
  const expected = [...expectedKeys].sort();
  return keys.length === expected.length && keys.every((key, index) => key === expected[index]);
}

function isPauseReason(value: unknown): value is PauseReason {
  return typeof value === "string" && PAUSE_REASONS.some((reason) => reason === value);
}

function isHeldAxis(value: unknown): value is HeldAxis {
  return value === -1 || value === 0 || value === 1;
}

export function parseNormalizedLifecycleEvent(value: unknown): NormalizedLifecycleEvent {
  if (isExactRecord(value, ["type", "reason"]) && value.type === "pause" && isPauseReason(value.reason)) {
    return Object.freeze({ type: "pause", reason: value.reason });
  }

  if (
    isExactRecord(value, ["type", "monotonicOriginMs"]) &&
    value.type === "resume" &&
    typeof value.monotonicOriginMs === "number" &&
    Number.isFinite(value.monotonicOriginMs) &&
    value.monotonicOriginMs >= 0
  ) {
    return Object.freeze({ type: "resume", monotonicOriginMs: value.monotonicOriginMs });
  }

  throw new TypeError("lifecycle event must be an exact normalized pause or resume event");
}

export function parseNormalizedHeldInput(value: unknown): Readonly<NormalizedHeldInput> {
  if (
    !isExactRecord(value, ["type", "movementX", "movementZ", "padCharge"]) ||
    value.type !== "normalized-held-input" ||
    !isHeldAxis(value.movementX) ||
    !isHeldAxis(value.movementZ) ||
    typeof value.padCharge !== "boolean"
  ) {
    throw new TypeError("held input must be an exact normalized held-input value");
  }

  return Object.freeze({
    type: "normalized-held-input",
    movementX: value.movementX,
    movementZ: value.movementZ,
    padCharge: value.padCharge,
  });
}
